import React from 'react';
import {Link} from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import * as FiIcons from 'react-icons/fi';

const {FiShield,FiMail,FiPhone,FiInfo}=FiIcons;

const PrivacyPolicyPage=()=> {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Privacy Policy - Diamond Cleaning Aylesbury"
        description="Diamond Cleaning Aylesbury privacy policy. How we collect, use, and protect your personal data. GDPR compliant data protection practices."
        image="https://diamondcleaning-aylesbury.com/img/domestic-homepage.webp"
      />
      <Header currentArea="Aylesbury" />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <SafeIcon icon={FiShield} className="w-16 h-16 text-white mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              We are committed to protecting your privacy and personal data. This policy explains how we collect, use, and safeguard your information.
            </p>
            <div className="text-blue-100">
              <p>Last updated: January 2024</p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">1. Introduction</h2>
            <div className="prose prose-lg text-gray-700 space-y-4">
              <p>
                Diamond Cleaning Aylesbury ("we", "our", "us") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
              <p>
                This policy applies to information we collect:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>On our website (diamondcleaning-aylesbury.co.uk)</li>
                <li>In email, text, and other electronic messages between you and us</li>
                <li>When you request our cleaning services</li>
                <li>Through mobile and desktop applications we provide</li>
              </ul>
            </div>
          </div>

          {/* Information We Collect */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">2. Information We Collect</h2>
            <div className="prose prose-lg text-gray-700 space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Information You Provide to Us</h3>
                <p>We may collect and process the following personal information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Contact Information:</strong> Name, email address, telephone number, postal address</li>
                  <li><strong>Service Information:</strong> Property details, cleaning requirements, preferred dates and times</li>
                  <li><strong>Payment Information:</strong> Billing address and payment method details (processed securely through third-party providers)</li>
                  <li><strong>Communication Records:</strong> Records of correspondence, feedback, and service requests</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Information Collected Automatically</h3>
                <p>When you visit our website, we may automatically collect:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Technical Information:</strong> IP address, browser type, device information, operating system</li>
                  <li><strong>Usage Information:</strong> Pages visited, time spent on pages, clicks, and navigation patterns</li>
                  <li><strong>Location Information:</strong> General location based on IP address (not precise location)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">2.3 Cookies and Tracking Technologies</h3>
                <p>
                  We use cookies and similar tracking technologies to enhance your experience on our website. You can control cookies through your browser settings, but disabling them may affect website functionality.
                </p>
              </div>
            </div>
          </div>

          {/* How We Use Information */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">3. How We Use Your Information</h2>
            <div className="prose prose-lg text-gray-700">
              <p>We use the information we collect for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Delivery:</strong> To provide, schedule, and manage our cleaning services</li>
                <li><strong>Communication:</strong> To respond to inquiries, provide quotes, and send service updates</li>
                <li><strong>Payment Processing:</strong> To process payments and manage billing</li>
                <li><strong>Customer Support:</strong> To provide customer service and resolve issues</li>
                <li><strong>Service Improvement:</strong> To analyze usage patterns and improve our services</li>
                <li><strong>Marketing:</strong> To send promotional materials (with your consent)</li>
                <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
              </ul>
            </div>
          </div>

          {/* Legal Basis for Processing */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">4. Legal Basis for Processing (GDPR)</h2>
            <div className="prose prose-lg text-gray-700">
              <p>Under the General Data Protection Regulation (GDPR), we process your personal data based on:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Contract Performance:</strong> Processing necessary to fulfill our cleaning service contract with you</li>
                <li><strong>Legitimate Interests:</strong> For business operations, fraud prevention, and service improvement</li>
                <li><strong>Consent:</strong> For marketing communications and non-essential cookies</li>
                <li><strong>Legal Obligation:</strong> To comply with tax, accounting, and other legal requirements</li>
              </ul>
            </div>
          </div>

          {/* Information Sharing */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">5. How We Share Your Information</h2>
            <div className="prose prose-lg text-gray-700">
              <p>We may share your personal information in the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Providers:</strong> With trusted third parties who assist in service delivery (payment processors, scheduling systems)</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>Consent:</strong> With your explicit consent for specific purposes</li>
              </ul>
              <p className="mt-4">
                <strong>We do not sell, rent, or lease your personal information to third parties for marketing purposes.</strong>
              </p>
            </div>
          </div>

          {/* Data Security */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">6. Data Security</h2>
            <div className="prose prose-lg text-gray-700">
              <p>We implement appropriate security measures to protect your personal information:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Secure data transmission using SSL encryption</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and staff training on data protection</li>
                <li>Secure storage of physical and digital records</li>
              </ul>
              <p className="mt-4">
                However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </div>
          </div>

          {/* Data Retention */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">7. Data Retention</h2>
            <div className="prose prose-lg text-gray-700">
              <p>We retain your personal information for as long as necessary to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide our services to you</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Meet tax and accounting requirements (typically 7 years)</li>
              </ul>
              <p className="mt-4">
                When personal information is no longer needed, we securely delete or anonymize it.
              </p>
            </div>
          </div>

          {/* Your Rights */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">8. Your Rights</h2>
            <div className="prose prose-lg text-gray-700">
              <p>Under GDPR and UK data protection law, you have the following rights:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data we hold</li>
                <li><strong>Rectification:</strong> Request correction of inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Request transfer of your data to another service provider</li>
                <li><strong>Restriction:</strong> Request limitation on how we process your data</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for marketing communications</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us using the details provided below.
              </p>
            </div>
          </div>

          {/* Third-Party Links */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">9. Third-Party Links</h2>
            <div className="prose prose-lg text-gray-700">
              <p>
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read their privacy policies before providing any personal information.
              </p>
            </div>
          </div>

          {/* Changes to Privacy Policy */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">10. Changes to This Privacy Policy</h2>
            <div className="prose prose-lg text-gray-700">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Continued use of our services after changes constitutes acceptance of the updated policy.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">11. Contact Us</h2>
            <div className="bg-gray-50 rounded-2xl p-8">
              <p className="text-gray-700 mb-6">
                If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Details</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-600" />
                      <span className="text-gray-700">diamonddomesticcleaning1@gmail.com</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiPhone} className="w-5 h-5 text-blue-600" />
                      <span className="text-gray-700">07878 522378</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Postal Address</h3>
                  <address className="text-gray-700 not-italic">
                    Diamond Cleaning Aylesbury<br />
                    5 Ossulbury Ln<br />
                    Aylesbury HP19 9DW<br />
                    Buckinghamshire, England
                  </address>
                </div>
              </div>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Data Protection Officer</h4>
                    <p className="text-blue-800 text-sm">
                      For data protection inquiries, you can also contact the Information Commissioner's Office (ICO) at ico.org.uk or call 0303 123 1113.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;